import {
    Text3,
    Text2,
    IconChevronLeftRegular,
    NavigationBar,
    IconWarningRegular,
    Stack,
    Text4,
    skinVars,
    useDialog,
} from '@telefonica/mistica';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { isBrowser } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import OTPVerification from '../../../../../components/OTPVerification';
import { sendContactVerificationOTP, validateContactVerificationOTP } from '../../../../../services/CustomerService';
import { setInfoError } from '../../../../../utils/dataTransformation/errorMesssages';
import FeedbackOTP from './feedbackOTP';
import ContactInfoVerificationStep1 from '../contactInfoVerificationStep1';
import './index.scss';
import { isWebviewAndroid, isWebviewIOS } from '../../../../../utils/navigation/controlDevices';
import { TrackingEvents } from '../../../../../utils/dataTransformation/tracking';

export interface ContactInfoVerificationOTPInterface {
    fromDesktop?: boolean;
    setComponentSelected?: any;
    typeVerificationDesktop?: string;
    currentMobileDesktop?: string;
    currentEmailDesktop?: string;
}

export const ContactInfoVerificationOTP: React.FC<ContactInfoVerificationOTPInterface> = ({
    fromDesktop,
    setComponentSelected,
    typeVerificationDesktop,
    currentMobileDesktop,
    currentEmailDesktop,
}: ContactInfoVerificationOTPInterface) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { trackingScreenView } = new TrackingEvents();
    const typeVerificationObd: any = fromDesktop ? typeVerificationDesktop : state?.typeVerification;
    const currentMobile: any = fromDesktop ? currentMobileDesktop : state?.currentMobile;
    const currentEmail: any = fromDesktop ? currentEmailDesktop : state?.currentEmail;
    const [otp, setOtp] = useState('');
    const [incorrectCode, setIncorrectCode] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState<any>(window.innerWidth);
    const { dialog } = useDialog();

    useEffect(() => {
        if (!(isWebviewAndroid || isWebviewIOS)) {
            trackingScreenView();
        }
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const sendToNative = (url: string, description: string, err?: any) => {
        const feedbackData = {
            stateFeedback: description,
            err: err,
        };
        navigate(url, { state: feedbackData });
    };

    const escalationOTPSend = () => {
        const contact = typeVerificationObd === 'mobile' ? currentMobile : currentEmail;
        sendContactVerificationOTP(contact)
            .then(() => {})
            .catch((error: any) => {
                sendToNative('/obd/verify/otp/feedback', 'ERROR', setInfoError(error));
            });
    };
    const escalationOTPValidate = () => {
        validateContactVerificationOTP(otp)
            .then((response: any) => {
                if (response.status === 200) {
                    if (fromDesktop) {
                        setComponentSelected(
                            <FeedbackOTP
                                fromDesktop={fromDesktop}
                                setComponentSelected={setComponentSelected}
                                stateFeedbackDesktop={'SUCCESS'}
                            ></FeedbackOTP>,
                        );
                    } else {
                        sendToNative('/obd/verify/otp/feedback', 'SUCCESS', '');
                    }
                }
            })
            .catch((error: AxiosError) => {
                const { id } = setInfoError(error);
                if ([400, 403].includes(Number(error.response?.status))) {
                    dialog({
                        icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={64} />,
                        title: t('profileFeedbackErrorTitle'),
                        message: (() => {
                            return (
                                <Stack space={16}>
                                    <Text3 light color={skinVars.colors.borderHigh}>
                                        {t('errorValidateOTP')}
                                    </Text3>
                                    {id && (
                                        <Text3 light color={skinVars.colors.borderHigh}>
                                            {`${t('codeError')} ${id}`}
                                        </Text3>
                                    )}
                                </Stack>
                            );
                        })() as any,
                        onAccept() {},
                        acceptText: t('buttonUnderstoodFeedback'),
                    });
                } else {
                    if (fromDesktop) {
                        setComponentSelected(
                            <FeedbackOTP
                                fromDesktop={fromDesktop}
                                setComponentSelected={setComponentSelected}
                                errDesktop={setInfoError(error)}
                                stateFeedbackDesktop={'ERROR'}
                            ></FeedbackOTP>,
                        );
                    } else {
                        sendToNative('/obd/verify/otp/feedback', 'ERROR', setInfoError(error));
                    }
                }
            });
    };

    return (
        <div className='contact-info-verification-otp'>
            {isWebviewAndroid || isWebviewIOS ? (
                <div
                    className='contact-info-verification-otp__header-navigationBar'
                    style={{
                        background: skinVars.colors.navigationBarBackground,
                        height: isWebviewAndroid || isWebviewIOS ? '44px' : windowWidth >= 1024 ? '80px' : '56px',
                    }}
                >
                    <Text4 regular color={skinVars.colors.textNavigationBarPrimary}>
                        {typeVerificationObd === 'mobile' ? t('titleMobileVerification') : t('titleEmailVerification')}
                    </Text4>
                </div>
            ) : (
                <div className='contact-info-verification-otp__header-navigationBar'>
                    <NavigationBar
                        isInverse={true}
                        title={
                            typeVerificationObd === 'mobile'
                                ? t('titleMobileVerification')
                                : t('titleEmailVerification')
                        }
                        onBack={() => navigate(-1)}
                    />
                </div>
            )}
            {isBrowser && (
                <div
                    className='contact-info-verification-otp__header-return'
                    onClick={() => {
                        setComponentSelected(
                            <ContactInfoVerificationStep1
                                fromDesktop={true}
                                setComponentSelected={setComponentSelected}
                            ></ContactInfoVerificationStep1>,
                        );
                    }}
                >
                    <div className='contact-info-verification-otp__header-return-icon'>
                        <IconChevronLeftRegular color={skinVars.colors.textLink} size={20} />
                    </div>
                    <Text2 medium color={skinVars.colors.textLink}>
                        {t('returnResponsive')}
                    </Text2>
                </div>
            )}
            <OTPVerification
                title={t('contactVerificationOTPtitle')}
                subtitle1={
                    typeVerificationObd === 'mobile'
                        ? t('contactVerificationOTPsubtitle1Mobile')
                        : t('contactVerificationOTPsubtitle1')
                }
                subtitle2={t('contactVerificationOTPsubtitle2')}
                contact={typeVerificationObd === 'mobile' ? currentMobile : currentEmail}
                handleButtonContinue={escalationOTPValidate}
                handleSendAgain={escalationOTPSend}
                setOTPValue={setOtp}
                incorrectCode={incorrectCode}
                typeVerificationContact={typeVerificationObd}
            ></OTPVerification>
        </div>
    );
};

export default ContactInfoVerificationOTP;
